/**
 * VoiceSettingsHeader - Top bar for voice and audio settings in Narration view
 *
 * Features:
 * - Voice selector with preview
 * - TTS Model toggle (Flash/Pro)
 * - Generate All Scripts button
 * - Generate All Audio button
 * - Apply All Audio button
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { VoiceSelector } from '@/components/slides/VoiceSelector';
import {
  Loader2,
  Sparkles,
  RefreshCw,
  Volume2,
  CheckCheck,
} from 'lucide-react';
import { type TTSVoice } from '@/services/audioService';

const SHOW_NARRATION_ADVANCED = import.meta.env.VITE_SHOW_NARRATION_ADVANCED === 'true';

interface VoiceSettingsHeaderProps {
  /** Available voices */
  voices: TTSVoice[];
  /** Currently selected voice ID */
  selectedVoice: string;
  /** Callback when voice changes */
  onVoiceChange: (voiceId: string) => void;
  /** @deprecated Model selection is no longer used - single TTS model is configured via env var */
  selectedModel?: 'flash' | 'pro';
  /** @deprecated Model selection is no longer used - single TTS model is configured via env var */
  onModelChange?: (model: 'flash' | 'pro') => void;
  /** Total number of scripts */
  totalScripts: number;
  /** Number of scripts with audio */
  scriptsWithAudio: number;
  /** Whether scripts are currently being generated */
  isGeneratingScripts: boolean;
  /** Whether audio is currently being generated */
  isGeneratingAudio: boolean;
  /** Number of audio files currently generating */
  audioGeneratingCount: number;
  /** Whether unified narrative context exists */
  hasNarrativeContext: boolean;
  /** Callback to generate all scripts */
  onGenerateScripts: () => void;
  /** Callback to generate all audio */
  onGenerateAudio: () => void;
  /** Whether controls should be disabled */
  disabled?: boolean;
  /** Compact mode for embedding in header bars */
  compact?: boolean;
  /** Optional className */
  className?: string;
  /** Callback to apply audio to all slides */
  onApplyAllAudio?: () => void;
  /** Whether audio is currently being applied to all slides */
  isApplyingAllAudio?: boolean;
  /** Number of slides that need audio applied */
  slidesNeedingApply?: number;
  /** Audio-first mode — hides voice/generate buttons, shows Sync All Animations */
  isAudioFirst?: boolean;
  /** Master audio duration for display in audio-first mode */
  masterAudioDuration?: number;
}

export const VoiceSettingsHeader: React.FC<VoiceSettingsHeaderProps> = ({
  voices,
  selectedVoice,
  onVoiceChange,
  // Model selection props are deprecated - kept for backward compatibility
  selectedModel: _selectedModel,
  onModelChange: _onModelChange,
  totalScripts,
  scriptsWithAudio,
  isGeneratingScripts,
  isGeneratingAudio,
  audioGeneratingCount,
  hasNarrativeContext,
  onGenerateScripts,
  onGenerateAudio,
  disabled,
  compact = false,
  className,
  onApplyAllAudio,
  isApplyingAllAudio,
  slidesNeedingApply = 0,
  isAudioFirst,
  masterAudioDuration,
}) => {
  const allAudioReady = scriptsWithAudio > 0 && scriptsWithAudio === totalScripts;
  const hasScripts = totalScripts > 0 && hasNarrativeContext;

  // Compact mode renders inline for header bars
  if (compact) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {/* Compact Voice Selector */}
        <VoiceSelector
          voices={voices}
          selectedVoice={selectedVoice}
          onVoiceChange={onVoiceChange}
          disabled={disabled || isGeneratingAudio}
          className="w-[140px]"
        />

        {SHOW_NARRATION_ADVANCED && (
          <>
            {/* Compact Generate Scripts */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onGenerateScripts}
              disabled={disabled || isGeneratingScripts || isGeneratingAudio}
              className="h-7 px-2 text-[10px] text-white/70 hover:text-white hover:bg-white/5"
            >
              {isGeneratingScripts ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : hasNarrativeContext ? (
                <RefreshCw className="w-3.5 h-3.5" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              <span className="ml-1">{isGeneratingScripts ? 'Gen...' : 'Scripts'}</span>
            </Button>

            {/* Compact Generate Audio */}
            {hasScripts && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onGenerateAudio}
                disabled={disabled || isGeneratingAudio || isGeneratingScripts}
                className="h-7 px-2 text-[10px] text-white/70 hover:text-white hover:bg-white/5"
              >
                {isGeneratingAudio ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : allAudioReady ? (
                  <RefreshCw className="w-3.5 h-3.5" />
                ) : (
                  <Volume2 className="w-3.5 h-3.5" />
                )}
                <span className="ml-1">
                  {isGeneratingAudio ? `${scriptsWithAudio}/${totalScripts}` : allAudioReady ? 'Regen' : 'Audio'}
                </span>
              </Button>
            )}

            {/* Compact Apply All */}
            {slidesNeedingApply > 0 && onApplyAllAudio && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onApplyAllAudio}
                disabled={disabled || isApplyingAllAudio}
                className="h-7 px-2 text-[10px] text-white/70 hover:text-white hover:bg-white/5"
              >
                {isApplyingAllAudio ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CheckCheck className="w-3.5 h-3.5" />
                )}
                <span className="ml-1">Apply ({slidesNeedingApply})</span>
              </Button>
            )}
          </>
        )}
      </div>
    );
  }

  // Normal (non-compact) mode
  // Audio-first mode: simplified header with master audio label + sync button
  if (isAudioFirst) {
    const formattedDuration = masterAudioDuration
      ? `${Math.floor(masterAudioDuration / 60)}:${String(Math.floor(masterAudioDuration % 60)).padStart(2, '0')}`
      : null;

    return (
      <div
        className={cn(
          "flex-shrink-0 px-4 py-3 border-b border-border/50 bg-muted/20",
          className
        )}
      >
        <div className="flex items-center justify-between gap-4">
          {/* Left section: Master Audio label */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Master Audio</span>
            </div>
            {formattedDuration && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {formattedDuration}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {totalScripts} slides
            </span>
          </div>

          {/* Right section: Sync All Animations button */}
          <div className="flex items-center gap-2">
            {SHOW_NARRATION_ADVANCED && onApplyAllAudio && (
              <Button
                variant="default"
                size="sm"
                onClick={onApplyAllAudio}
                disabled={disabled || isApplyingAllAudio}
              >
                {isApplyingAllAudio ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    Sync All Animations
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex-shrink-0 px-4 py-3 border-b border-border/50 bg-muted/20",
        className
      )}
    >
      <div className="flex items-center justify-between gap-4">
        {/* Left section: Voice settings */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Voice</span>
          </div>

          {/* Voice Selector */}
          <VoiceSelector
            voices={voices}
            selectedVoice={selectedVoice}
            onVoiceChange={onVoiceChange}
            disabled={disabled || isGeneratingAudio}
            className="w-[180px]"
          />
        </div>

        {/* Right section: Action buttons */}
        {SHOW_NARRATION_ADVANCED && (
          <div className="flex items-center gap-2">
            {/* Generate Scripts button */}
            <Button
              variant={hasNarrativeContext ? 'outline' : 'default'}
              size="sm"
              onClick={onGenerateScripts}
              disabled={disabled || isGeneratingScripts || isGeneratingAudio}
            >
              {isGeneratingScripts ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Generating...
                </>
              ) : hasNarrativeContext ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  Regenerate Scripts
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  Generate All Scripts
                </>
              )}
            </Button>

            {/* Generate Audio button - only show when scripts exist */}
            {hasScripts && (
              <Button
                variant={allAudioReady ? 'outline' : 'default'}
                size="sm"
                onClick={onGenerateAudio}
                disabled={disabled || isGeneratingAudio || isGeneratingScripts}
              >
                {isGeneratingAudio ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Generating... ({scriptsWithAudio}/{totalScripts})
                  </>
                ) : allAudioReady ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    Regenerate Audio
                  </>
                ) : scriptsWithAudio > 0 ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    Generate Remaining ({totalScripts - scriptsWithAudio})
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    Generate All Audio
                  </>
                )}
              </Button>
            )}

            {/* Apply All button - only show when there are slides needing audio applied */}
            {slidesNeedingApply > 0 && onApplyAllAudio && (
              <Button
                variant="outline"
                size="sm"
                onClick={onApplyAllAudio}
                disabled={disabled || isApplyingAllAudio || isGeneratingAudio || isGeneratingScripts}
                className={cn(
                  "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300 dark:border-emerald-500/30 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
                )}
              >
                {isApplyingAllAudio ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    <CheckCheck className="w-4 h-4 mr-1.5" />
                    Apply All ({slidesNeedingApply})
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Progress bar when generating audio */}
      {isGeneratingAudio && (
        <div className="mt-3 space-y-1.5">
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{
                width: `${totalScripts > 0 ? (scriptsWithAudio / totalScripts) * 100 : 0}%`,
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground text-center">
            {audioGeneratingCount} generating, {scriptsWithAudio} completed of {totalScripts}
          </p>
        </div>
      )}
    </div>
  );
};

export default VoiceSettingsHeader;
